import * as vp from 'vopay';
import moment from 'moment';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import useFetchPortalVideos from '../../../../hooks/useApi/useFetchPortalVideos';
import VideoSearchForm from './VideoSearchForm';
import VideoSearchResult from './VideoSearchResult';
import styles from './VideoContainer.module.scss';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useTranslation } from 'react-i18next';

type FC = FunctionComponent;

type VideoPlayerProps = {
  video: vp.VideoObject;
};

const { Box, Title, BackButton } = vp;

const VideoContainer: FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'VideoContainer' });
  const [isDropdownSelected, setIsDropdownSelected] = useState(false);
  const [hasSearchResult, setHasSearchResult] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredByTabLists, setFilteredByTabLists] = useState<vp.VideoObject[]>([]);
  const [filteredByKeyWordLists, setFilteredByKeyWordLists] = useState<{ [key: string]: vp.VideoObject }>({});
  const [selectedVideo, setSelectedVideo] = useState<vp.VideoObject>({
    title: '',
    url: '',
    thumbnail: '',
    category: '',
    length: '',
    lastModified: '',
    portal: undefined,
    sort_order: 0,
    requiredFeatures: [],
    accountTypes: [],
  });
  const [videos, fetchPortalVideos] = useFetchPortalVideos({});
  const [filteredVideoForSearch, setFilteredVideoForSearch] = useState<vp.VideoObject[]>([]);

  const {
    response: { partnerTypeLabel },
  } = usePartnerData();

  // requiredFeatures not checked here — PartnerData.provider exposes no account feature flags
  const isVideoVisible = (video: vp.VideoObject) => {
    return (
      !video.accountTypes?.length ||
      video.accountTypes.some((type) => type.toLowerCase() === partnerTypeLabel.toLowerCase())
    );
  };

  useEffect(() => {
    fetchPortalVideos({});
  }, []);

  useEffect(() => {
    if ((videos.VideoList || []).length >= 1) {
      const visible = videos.VideoList.filter((video) => isVideoVisible(video));
      setFilteredVideoForSearch(visible);
      const toOrder = (v: number | undefined) => (v ? v : Infinity);
      const sorted = [...visible].sort((a, b) => {
        const catA = toOrder(videos.Categories.find((c) => c.Category === a.category)?.sort_order);
        const catB = toOrder(videos.Categories.find((c) => c.Category === b.category)?.sort_order);
        return catA !== catB ? catA - catB : toOrder(a.sort_order) - toOrder(b.sort_order);
      });
      setFilteredByTabLists(sorted);
      if (!isDropdownSelected) {
        if (sorted[0]) setSelectedVideo(sorted[0]);
      }
    }
  }, [videos]);

  useEffect(() => {
    setHasSearchResult(false);
  }, [selectedVideo]);

  useEffect(() => {
    if (searchTerm === '') setHasSearchResult(false);
  }, [searchTerm]);

  const getSelectedVideo = (video: vp.VideoObject) => {
    setSelectedVideo(video);
    setIsDropdownSelected(true);
    setHasSearchResult(false);
    setSearchTerm('');
  };

  const VideoPlayer = ({ video }: VideoPlayerProps) => (
    <>
      <h4>{video?.title}</h4>
      <div className={styles.videoPlayer}>
        <video
          controls
          width="100%"
          height="100%"
          muted
          autoPlay={false}
          poster={video?.thumbnail}
          controlsList="nodownload"
        >
          <source src={video?.url} type="video/mp4" />
          {t('videoFallback', { defaultValue: "Sorry, your browser doesn't support embedded videos." })}
        </video>
      </div>
    </>
  );

  const MemoizedVideoPlayer = useMemo(() => <VideoPlayer video={selectedVideo} />, [selectedVideo, t]);

  return (
    <Box flx="col">
      <Box mb="lg" jc="fs" ai="center" gap="common">
        <BackButton variant="arrow" backButtonEnabled />
        <Title title={t('title', { defaultValue: 'Video Tutorials' })} />
      </Box>
      <Box flx="row" jc="sb" ai="center" mt="md" gap="md">
        <Box lineHeight="25px">
          {t('description', {
            defaultValue: 'Explore step-by-step videos designed to guide you through the VoPay Portal with ease.',
          })}
        </Box>
        <VideoSearchForm
          videos={filteredVideoForSearch}
          filteredByKeyWordLists={filteredByKeyWordLists}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setFilteredByKeyWordLists={setFilteredByKeyWordLists}
          setHasSearchResult={setHasSearchResult}
          getSelectedVideo={getSelectedVideo}
        />
      </Box>

      {(videos.VideoList || []).length >= 1 && (
        <div className={styles.guideContainer}>
          <div className={styles.guideListsContainer}>
            <div className={styles.guideScrollableList}>
              {filteredByTabLists.map((video) => (
                <button
                  key={video.url}
                  type="button"
                  className={[styles.guideList, styles[getActiveClass(video.title)]].join(' ')}
                  onClick={() => {
                    setSelectedVideo(video);
                  }}
                >
                  <div className={styles.thumbnail}>
                    <div className={styles.length}>{String(video.length)?.replace('.', ':')}</div>
                    <img
                      src={video.thumbnail}
                      alt=""
                      onError={(e) => {
                        try {
                          e.currentTarget.src = `${new URL(import.meta.env.VITE_LEARNING_CENTRE_BASE_URL).origin}/wp-content/uploads/default-thumbnail.png`;
                        } catch {
                          e.currentTarget.style.display = 'none';
                        }
                      }}
                    />
                  </div>
                  <div className={styles.details}>
                    <h5>{video.title}</h5>
                    <span>{moment(video.lastModified).format('MMMM D, YYYY')}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {hasSearchResult ? (
            <VideoSearchResult
              searchTerm={searchTerm}
              filteredByKeyWordLists={filteredByKeyWordLists}
              getSelectedVideo={getSelectedVideo}
            />
          ) : (
            <div className={styles.guide}>{selectedVideo?.title !== '' && MemoizedVideoPlayer}</div>
          )}
        </div>
      )}
    </Box>
  );

  function getActiveClass(title: string) {
    return title === selectedVideo.title && !hasSearchResult ? 'active' : '';
  }
};

export default VideoContainer;
